import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  KibAccordionNew,
  KibAccordionNewItem,
} from '@chewy/kib-content-groups-react'
import { KibButtonNew } from '@chewy/kib-controls-react'
import KibTag from '@chewy/kib-tag-react'
import { DateUtils, LanguageUtils, Utils } from '@pbt/pbt-ui-components'

import type { DashboardLabTestEntry } from '~/api/graphql/generated/types'
import OverlayPanel from '~/components/common/OverlayPanel'
import {
  parseLabTestIdentifier,
  parseLabTestIdFromIdentifier,
} from '~/components/dashboard/lab-tests-dashboard/lab-tests-table/labTestUtils'
import {
  ColorVariantToTheme,
  LabStatusToColorVariant,
} from '~/constants/colors'
import {
  fetchLabTestDashboardDetails,
  fetchLabTestDetails,
  getAllLabTestsIsLoading,
  getLabTest,
  saveLabOrder,
} from '~/store/duck/labTestsDashboard'
import {
  useGetLabResultPreviewActions,
  useGetOrderPreviewActions,
} from '~/store/hooks/labTestsDashboard'
import {
  getLabOrderStatuses,
  getLabTestTypes,
  getLabVendors,
} from '~/store/reducers/constants'

import { useRightRailOverlay } from '../../context/RightRailOverlayContext'
import FileViewer from './FileViewer'
import LabTestItem from './LabTestItem'

import '@chewy/kib-content-groups-styles/dist/styles/kib-accordion-new.css'
import styles from './LabTestDetailsOverlay.module.scss'

interface LabTestDetailsOverlayProps {
  labTest: DashboardLabTestEntry
}

const LabTestDetailsOverlay = ({
  labTest: originalLabTest,
}: LabTestDetailsOverlayProps) => {
  const { t } = useTranslation(['Clients', 'Common', 'LabTests'])
  const dispatch = useDispatch()
  const { closeOverlay } = useRightRailOverlay()

  const labHasOrder = Boolean(originalLabTest.order?.id)
  const stripLabSuffix = (identifier: string) =>
    identifier.replace(/-L:\w+$/, '')
  const originalIdentifier = labHasOrder
    ? stripLabSuffix(originalLabTest.uniqueIdentifier || '')
    : originalLabTest.uniqueIdentifier
  const labTest = useSelector(getLabTest(originalIdentifier || '')) || {}
  const [updatedLabTest, setUpdatedLabTest] = useState(labTest)
  const [isOpen, setIsOpen] = useState(false)
  const isLoading = useSelector(getAllLabTestsIsLoading)
  const labTestIdentifier = labTest.identifier
  const isFetchingLabTest = !labTestIdentifier
  const labTestLogId = parseLabTestIdFromIdentifier(
    originalLabTest.uniqueIdentifier || '',
  )
  const LabTestTypesList = useSelector(getLabTestTypes)
  const LabVendorsList = useSelector(getLabVendors)
  const orderingVet = labTest.vet?.name
  const orderDate = labTest.orderedDate
  const LabOrderStatusesList = useSelector(getLabOrderStatuses)

  useEffect(() => {
    if (!updatedLabTest.identifier) {
      setUpdatedLabTest(labTest)
    }
  }, [labTest.identifier])

  useEffect(() => {
    if (labHasOrder) {
      // The new endpoint does not support labs with orders, so we need to use the old endpoint
      const { vendorId, orderId, soapId, invoiceId, patientId, vetId } =
        parseLabTestIdentifier(originalLabTest.uniqueIdentifier || '')
      dispatch(
        fetchLabTestDetails(
          vendorId,
          orderId,
          soapId,
          invoiceId,
          patientId,
          vetId,
        ),
      )
    } else if (labTestLogId) {
      dispatch(fetchLabTestDashboardDetails(labTestLogId))
    }
  }, [originalLabTest.uniqueIdentifier, labHasOrder, labTestLogId])

  const getHasUnsavedChanges = () => {
    const originalLogs = labTest.tests || []
    const updatedLogs = updatedLabTest.tests || []

    // Check if any values changed
    return updatedLogs.some((updated) => {
      const original = originalLogs.find((log) => log.id === updated.id)
      if (
        !original ||
        original.statusId !== updated.statusId ||
        original.notes !== updated.notes
      ) {
        return true
      }

      return false
    })
  }
  const hasUnsavedChanges = getHasUnsavedChanges()

  const updateLabTestEntry = useCallback(
    (
      entryId: string,
      updates: Partial<{
        notes: string
        statusId: string
      }>,
    ) => {
      setUpdatedLabTest((prev) => ({
        ...prev,
        tests: prev.tests?.map((entry) =>
          entry.id === entryId ? { ...entry, ...updates } : entry,
        ),
      }))
    },
    [],
  )

  const labType = LanguageUtils.getConstantTranslatedName(
    labTest.tests?.[0]?.typeId,
    LabTestTypesList,
  )
  const vendorName = LanguageUtils.getConstantTranslatedName(
    labTest.vendorId,
    LabVendorsList,
  )
  const labName = `${vendorName}${labType ? `: ${labType}` : ''}`
  const stateName = Utils.getConstantName(
    labTest.order?.statusId,
    LabOrderStatusesList,
  )

  const getOrderPreviewActions = useGetOrderPreviewActions()

  const availableOrderActions = useMemo(
    () =>
      getOrderPreviewActions({
        soapBusinessId: labTest.businessId,
        order: labTest.order,
        vendorId: labTest.vendorId,
        soapId: labTest.soapId,
      }),
    [
      getOrderPreviewActions,
      labTest.businessId,
      labTest.order,
      labTest.vendorId,
      labTest.soapId,
    ],
  )

  const getLabResultPreviewActions = useGetLabResultPreviewActions()
  const availableResultsActions = useMemo(
    () =>
      getLabResultPreviewActions({
        order: labTest.order,
        vendorId: labTest.vendorId,
        clientId: labTest.client,
        patientId: labTest.patient,
        soapId: labTest.soapId,
      }),
    [
      getLabResultPreviewActions,
      labTest.order,
      labTest.vendorId,
      labTest.patient,
      labTest.client,
      labTest.soapId,
    ],
  )

  const hasOrderActions = Boolean(availableOrderActions)
  const showPrint = Boolean(availableOrderActions?.print)
  const showPreview = Boolean(availableOrderActions?.preview)

  const resultPdfUrl = labTest.order?.resultPdfUrl

  const bodyContent = (
    <div className={styles.container}>
      <div className={styles.topSection}>
        <div className={styles.examsSection}>
          {isFetchingLabTest && <div className={styles.loadingContent} />}
          {!isFetchingLabTest && labTest.tests && labTest.tests.length > 0 && (
            <div className={styles.examsList}>
              {labTest.tests.map((test) => (
                <div className={styles.examItem} key={test.id}>
                  <span className={styles.examName}>{test.name}</span>
                </div>
              ))}
            </div>
          )}
          {isFetchingLabTest ? (
            <div className={styles.loadingContent} />
          ) : (
            <span className={styles.orderedByText}>
              {orderingVet
                ? t('LabTests:LAB_TEST_DETAILS.ORDERED_BY_VET_NAME_ON_DATE', {
                    vetName: orderingVet,
                    date: DateUtils.formatDate(orderDate),
                  })
                : t('LabTests:LAB_TEST_DETAILS.ORDERED_ON_DATE', {
                    date: DateUtils.formatDate(orderDate),
                  })}
            </span>
          )}
        </div>

        {hasOrderActions && (
          <div className={styles.orderActionsContainer}>
            {showPrint && availableOrderActions?.print && (
              <KibButtonNew
                emphasis="tertiary"
                size="small"
                theme="utility"
                onClick={availableOrderActions.print.handle}
              >
                {t('Common:PRINT_ORDER')}
              </KibButtonNew>
            )}
            {showPreview && availableOrderActions?.preview && (
              <KibButtonNew
                emphasis="tertiary"
                size="small"
                theme="utility"
                onClick={availableOrderActions.preview.handle}
              >
                {t('Common:VIEW_ORDER')}
              </KibButtonNew>
            )}
          </div>
        )}
      </div>

      {resultPdfUrl && (
        <div className={styles.middleSection}>
          <KibAccordionNew>
            <KibAccordionNewItem
              className={styles.accordionItem}
              description={`${t('Common:LAST_UPDATED')} ${DateUtils.formatDate(labTest.orderedDate)}`}
              open={isOpen}
              subtitle={
                <KibTag
                  theme={
                    ColorVariantToTheme[LabStatusToColorVariant[stateName]]
                  }
                >
                  <span className={styles.statusText}>{stateName}</span>
                </KibTag>
              }
              title={t('Common:LAB_TEST_RESULTS')}
              onToggle={() => {
                setIsOpen(!isOpen)
              }}
            >
              <div className={styles.fileViewerContainer}>
                <FileViewer
                  disableDownload={!availableResultsActions?.print}
                  disableEmail={!availableResultsActions?.email}
                  disablePrint={!availableResultsActions?.print}
                  fileUrl={resultPdfUrl}
                  fitHeightToDocument={isOpen}
                  onEmail={
                    availableResultsActions?.email
                      ? availableResultsActions.email.handle
                      : undefined
                  }
                  onPrint={
                    availableResultsActions?.print
                      ? availableResultsActions.print.handle
                      : undefined
                  }
                />
              </div>
            </KibAccordionNewItem>
          </KibAccordionNew>
        </div>
      )}

      <div className={styles.bottomSection}>
        <div className={styles.testsContainer}>
          {isFetchingLabTest && <div className={styles.loadingItem} />}
          {!isFetchingLabTest &&
            (updatedLabTest.tests && updatedLabTest.tests.length > 0 ? (
              updatedLabTest.tests.map((test) => (
                <LabTestItem
                  disabled={isLoading}
                  key={test.id}
                  labTestIdentifier={labTestIdentifier}
                  labTestItem={test}
                  notes={test.notes || ''}
                  selectedStatusId={test.statusId || ''}
                  testId={test.id}
                  vendorId={labTest.vendorId || ''}
                  onNotesChange={(notes) =>
                    updateLabTestEntry(test.id, { notes })
                  }
                  onStatusChange={(statusId) =>
                    updateLabTestEntry(test.id, { statusId })
                  }
                />
              ))
            ) : (
              <div className={styles.sectionPlaceholder}>
                <p>{t('LabTests:NO_INDIVIDUAL_TESTS_AVAILABLE')}</p>
              </div>
            ))}
        </div>
      </div>
    </div>
  )

  const handleSaveChanges = () => {
    dispatch(saveLabOrder(updatedLabTest))
  }

  const handleClose = () => {
    closeOverlay()
  }

  return (
    <OverlayPanel
      bodyContent={bodyContent}
      handleClose={handleClose}
      isLoading={isFetchingLabTest}
      primaryButton={{
        label: isLoading ? t('Common:SAVING') : t('Common:SAVE_CHANGES'),
        onClick: handleSaveChanges,
        disabled: !hasUnsavedChanges || isLoading,
      }}
      title={labName}
    />
  )
}

export default LabTestDetailsOverlay
